/**
 * Tournament Simulation
 *
 * Extends the round simulation system to support multi-player tournaments
 * with golfer profiles, course fit calculations, weather effects, and cuts.
 */

import {
    HANDICAP_SCORING_PROFILES,
    HOLE_TYPE_ADJUSTMENTS,
    SCORE_TYPES,
    STANDARD_HOLE_LAYOUT
} from './golfConfig';
import { calculateCourseHandicap, getScoringProbabilities } from './golfSimulation';
import { getHoleTypeAdjustments } from './golferProfiles';
import { calculateCourseFit, calculateWeatherImpact, calculateFitAdjustment } from './courseProfiles';

/**
 * Tournament configuration schema
 */
export const createTournamentConfig = (overrides = {}) => {
    // Determine cut position based on rule and custom setting
    let cutPosition = 70;
    if (overrides.cutRule === 'top50AndTies') {
        cutPosition = 50;
    } else if (overrides.cutRule === 'topN') {
        cutPosition = overrides.customCutPosition ?? overrides.cutPosition ?? 65;
    } else if (overrides.cutRule === 'top70AndTies') {
        cutPosition = 70;
    } else if (overrides.cutRule === 'bottomPercentage') {
        // cutPosition will be resolved dynamically in applyCut based on field size
        cutPosition = overrides.cutPercentage ?? 40;
    } else {
        cutPosition = overrides.cutPosition ?? 70;
    }

    return {
        id: overrides.id || `tournament-${Date.now()}`,
        name: overrides.name || "Tournament",
        rounds: overrides.rounds ?? 4,
        cutAfterRound: overrides.cutAfterRound ?? 2,
        cutRule: overrides.cutRule || "top70AndTies",  // top70AndTies, top50AndTies, topN
        cutPosition,
        prestigeLevel: overrides.prestigeLevel || "regular",  // regular, elevated, major
        conditions: overrides.conditions || null  // Will be generated if not provided
    };
};

/**
 * Tournament prestige affects pressure calculations
 */
export const PRESTIGE_LEVELS = {
    regular: { label: "Regular Event", pressureMultiplier: 1.0 },
    elevated: { label: "Elevated Event", pressureMultiplier: 1.3 },
    major: { label: "Major Championship", pressureMultiplier: 1.6 }
};

/**
 * Apply golfer skill adjustments to scoring probabilities
 * Skills shift the probability distribution based on hole type emphasis
 */
function applySkillAdjustments(probabilities, golfer, hole, course) {
    const { skills } = golfer;
    const holeAdjustments = getHoleTypeAdjustments(golfer);
    const holePar = hole.par;

    // Get hole-type adjustment from golfer's skill profile
    const holeTypeKey = `par${holePar}`;
    const baseAdjustment = holeAdjustments[holeTypeKey] || 0;

    // Calculate skill emphasis for this hole
    // Par 3s emphasise approach and short game
    // Par 4s are balanced
    // Par 5s emphasise driving
    let skillBonus = 0;
    switch (holePar) {
        case 3:
            skillBonus = (skills.approach * 0.4 + skills.aroundGreen * 0.3 + skills.putting * 0.3) * 0.1;
            break;
        case 4:
            skillBonus = (skills.offTheTee * 0.25 + skills.approach * 0.35 +
                skills.aroundGreen * 0.2 + skills.putting * 0.2) * 0.1;
            break;
        case 5:
            skillBonus = (skills.offTheTee * 0.45 + skills.approach * 0.3 +
                skills.aroundGreen * 0.15 + skills.putting * 0.1) * 0.1;
            break;
        default:
            break;
    }

    const totalAdjustment = baseAdjustment + skillBonus;

    // Skill bonus shifts probabilities towards better scores
    // Positive adjustment = shift towards birdie/par
    // Negative adjustment = shift towards bogey/double
    const adjusted = { ...probabilities };

    if (totalAdjustment > 0) {
        // Shift towards better scores
        const shift = totalAdjustment * 0.15;
        adjusted[SCORE_TYPES.BIRDIE] = Math.min(0.5, adjusted[SCORE_TYPES.BIRDIE] + shift * 0.4);
        adjusted[SCORE_TYPES.PAR] = Math.min(0.8, adjusted[SCORE_TYPES.PAR] + shift * 0.4);
        adjusted[SCORE_TYPES.BOGEY] = Math.max(0.02, adjusted[SCORE_TYPES.BOGEY] - shift * 0.5);
        adjusted[SCORE_TYPES.DOUBLE_BOGEY] = Math.max(0.01, adjusted[SCORE_TYPES.DOUBLE_BOGEY] - shift * 0.3);
    } else if (totalAdjustment < 0) {
        // Shift towards worse scores
        const shift = Math.abs(totalAdjustment) * 0.15;
        adjusted[SCORE_TYPES.BIRDIE] = Math.max(0.005, adjusted[SCORE_TYPES.BIRDIE] - shift * 0.3);
        adjusted[SCORE_TYPES.PAR] = Math.max(0.1, adjusted[SCORE_TYPES.PAR] - shift * 0.4);
        adjusted[SCORE_TYPES.BOGEY] = Math.min(0.6, adjusted[SCORE_TYPES.BOGEY] + shift * 0.4);
        adjusted[SCORE_TYPES.DOUBLE_BOGEY] = Math.min(0.4, adjusted[SCORE_TYPES.DOUBLE_BOGEY] + shift * 0.3);
    }

    // Normalise
    const sum = Object.values(adjusted).reduce((a, b) => a + b, 0);
    for (const scoreType of Object.values(SCORE_TYPES)) {
        adjusted[scoreType] = adjusted[scoreType] / sum;
    }

    return adjusted;
}

/**
 * Calculate pressure effect on scoring
 * Players near the lead in later rounds face pressure
 */
function calculatePressureEffect(golfer, position, roundNumber, totalRounds, prestigeLevel) {
    // Pressure only matters in final rounds when in contention
    if (roundNumber < totalRounds - 1 || position > 15) return 0;

    const prestige = PRESTIGE_LEVELS[prestigeLevel] || PRESTIGE_LEVELS.regular;

    // Base pressure: 0.4 strokes for leader in final round of major
    const basePressure = 0.4 * prestige.pressureMultiplier;

    // Scale by position (leader feels most, 15th feels almost none)
    const positionFactor = Math.max(0, (16 - position) / 15);

    // Final round multiplier
    const roundFactor = roundNumber === totalRounds ? 1.0 : 0.5;

    // Golfer's pressure handling (-0.5 to +0.5)
    // Negative = chokes, positive = clutch
    const handling = golfer.situational?.pressureHandling ?? 0;

    // Net pressure (can be negative if golfer is clutch)
    return basePressure * positionFactor * roundFactor * (1 - handling);
}

/**
 * Convert score type to strokes relative to par
 */
function scoreTypeToStrokes(scoreType) {
    switch (scoreType) {
        case SCORE_TYPES.EAGLE_OR_BETTER: return -2;
        case SCORE_TYPES.BIRDIE: return -1;
        case SCORE_TYPES.PAR: return 0;
        case SCORE_TYPES.BOGEY: return 1;
        case SCORE_TYPES.DOUBLE_BOGEY: return 2;
        case SCORE_TYPES.TRIPLE_OR_WORSE: return 3;
        default: return 0;
    }
}

/**
 * Get result label from strokes relative to par
 */
function getResultLabel(strokesRelativeToPar) {
    if (strokesRelativeToPar <= -2) return 'eagle';
    if (strokesRelativeToPar === -1) return 'birdie';
    if (strokesRelativeToPar === 0) return 'par';
    if (strokesRelativeToPar === 1) return 'bogey';
    if (strokesRelativeToPar === 2) return 'double';
    return 'triple+';
}

/**
 * Simulate a single hole for a golfer in tournament context
 */
function simulateTournamentHole(golfer, hole, course, conditions, roundContext) {
    const { roundNumber, position, totalRounds, prestigeLevel } = roundContext;

    // Transform consistency from profile scale (0.7-1.4) to probability scale (0.15-0.35)
    // Profile: 0.7 = very consistent, 1.0 = average, 1.4 = erratic
    // Probability: 0.15 = very consistent, 0.25 = average, 0.35 = erratic
    const effectiveConsistency = 0.25 * golfer.consistency;

    // Base probabilities from handicap
    let probabilities = getScoringProbabilities(
        golfer.handicapIndex,
        hole.par,
        effectiveConsistency
    );

    // Apply golfer skill adjustments
    probabilities = applySkillAdjustments(probabilities, golfer, hole, course);

    // Calculate adjustments
    const courseFit = calculateCourseFit(golfer, course);
    const fitAdjustment = calculateFitAdjustment(courseFit);
    const weatherImpact = conditions ? calculateWeatherImpact(conditions, golfer, course) : 0;
    const pressureEffect = calculatePressureEffect(
        golfer, position, roundNumber, totalRounds, prestigeLevel
    );

    // Apply course fit to probabilities
    // Positive courseFit = good fit = shift towards better scores
    // Negative courseFit = poor fit = shift towards worse scores
    if (Math.abs(courseFit) > 0.02) {
        const fitMagnitude = Math.abs(courseFit) * 0.12; // Scale factor for fit impact

        if (courseFit > 0) {
            // Good fit - shift towards better scores
            probabilities[SCORE_TYPES.BIRDIE] = Math.min(0.5, probabilities[SCORE_TYPES.BIRDIE] + fitMagnitude * 0.4);
            probabilities[SCORE_TYPES.PAR] = Math.min(0.8, probabilities[SCORE_TYPES.PAR] + fitMagnitude * 0.3);
            probabilities[SCORE_TYPES.BOGEY] = Math.max(0.02, probabilities[SCORE_TYPES.BOGEY] - fitMagnitude * 0.4);
            probabilities[SCORE_TYPES.DOUBLE_BOGEY] = Math.max(0.01, probabilities[SCORE_TYPES.DOUBLE_BOGEY] - fitMagnitude * 0.3);
        } else {
            // Poor fit - shift towards worse scores
            probabilities[SCORE_TYPES.BIRDIE] = Math.max(0.005, probabilities[SCORE_TYPES.BIRDIE] - fitMagnitude * 0.3);
            probabilities[SCORE_TYPES.PAR] = Math.max(0.1, probabilities[SCORE_TYPES.PAR] - fitMagnitude * 0.3);
            probabilities[SCORE_TYPES.BOGEY] = Math.min(0.6, probabilities[SCORE_TYPES.BOGEY] + fitMagnitude * 0.4);
            probabilities[SCORE_TYPES.DOUBLE_BOGEY] = Math.min(0.4, probabilities[SCORE_TYPES.DOUBLE_BOGEY] + fitMagnitude * 0.3);
        }

        // Normalise probabilities
        const fitSum = Object.values(probabilities).reduce((a, b) => a + b, 0);
        for (const scoreType of Object.values(SCORE_TYPES)) {
            probabilities[scoreType] = probabilities[scoreType] / fitSum;
        }
    }

    // Apply weather impact to probabilities
    if (weatherImpact > 0.3) {
        // Significant weather shifts towards worse scores
        const weatherShift = (weatherImpact - 0.3) * 0.1;
        probabilities[SCORE_TYPES.BIRDIE] = Math.max(0.005, probabilities[SCORE_TYPES.BIRDIE] - weatherShift * 0.3);
        probabilities[SCORE_TYPES.PAR] = Math.max(0.1, probabilities[SCORE_TYPES.PAR] - weatherShift * 0.3);
        probabilities[SCORE_TYPES.BOGEY] = Math.min(0.6, probabilities[SCORE_TYPES.BOGEY] + weatherShift * 0.4);
        probabilities[SCORE_TYPES.DOUBLE_BOGEY] = Math.min(0.4, probabilities[SCORE_TYPES.DOUBLE_BOGEY] + weatherShift * 0.2);

        // Normalise
        const sum = Object.values(probabilities).reduce((a, b) => a + b, 0);
        for (const scoreType of Object.values(SCORE_TYPES)) {
            probabilities[scoreType] = probabilities[scoreType] / sum;
        }
    }

    // Select score type based on probabilities
    const rand = Math.random();
    let cumulative = 0;
    let selectedScoreType = SCORE_TYPES.PAR;

    for (const [scoreType, probability] of Object.entries(probabilities)) {
        cumulative += probability;
        if (rand < cumulative) {
            selectedScoreType = scoreType;
            break;
        }
    }

    const strokesRelativeToPar = scoreTypeToStrokes(selectedScoreType);
    const score = hole.par + strokesRelativeToPar;

    return {
        hole: hole.number,
        par: hole.par,
        score,
        relativeToPar: strokesRelativeToPar,
        result: getResultLabel(strokesRelativeToPar),
        adjustments: {
            courseFit: Math.round(fitAdjustment * 100) / 100,
            weather: Math.round(weatherImpact * 100) / 100,
            pressure: Math.round(pressureEffect * 100) / 100
        }
    };
}

/**
 * Simulate a tournament round for a golfer
 */
export function simulateTournamentRound(golfer, course, conditions, roundContext) {
    const holes = course.holes || STANDARD_HOLE_LAYOUT;
    const holeResults = holes.map(hole =>
        simulateTournamentHole(golfer, hole, course, conditions, roundContext)
    );

    const totalScore = holeResults.reduce((sum, h) => sum + h.score, 0);
    const frontNine = holeResults.slice(0, 9).reduce((sum, h) => sum + h.score, 0);
    const backNine = holeResults.slice(9, 18).reduce((sum, h) => sum + h.score, 0);

    // Count score types
    const scoreCounts = {
        eagles: holeResults.filter(h => h.relativeToPar <= -2).length,
        birdies: holeResults.filter(h => h.relativeToPar === -1).length,
        pars: holeResults.filter(h => h.relativeToPar === 0).length,
        bogeys: holeResults.filter(h => h.relativeToPar === 1).length,
        doubleBogeys: holeResults.filter(h => h.relativeToPar === 2).length,
        tripleOrWorse: holeResults.filter(h => h.relativeToPar >= 3).length
    };

    // Aggregate adjustments
    const avgAdjustments = {
        courseFit: holeResults[0]?.adjustments?.courseFit || 0,
        weather: holeResults.reduce((sum, h) => sum + (h.adjustments?.weather || 0), 0) / 18,
        pressure: holeResults.reduce((sum, h) => sum + (h.adjustments?.pressure || 0), 0) / 18
    };

    return {
        golferId: golfer.id,
        golferName: golfer.name,
        roundNumber: roundContext.roundNumber,
        totalScore,
        relativeToPar: totalScore - course.par,
        frontNine,
        backNine,
        ...scoreCounts,
        holes: holeResults,
        conditions,
        adjustments: avgAdjustments
    };
}

/**
 * Calculate leaderboard standings after a round
 */
function calculateStandings(golfers, roundResults, course) {
    // Aggregate scores per golfer
    const aggregated = golfers.map(golfer => {
        const rounds = roundResults.filter(r => r.golferId === golfer.id);
        const totalStrokes = rounds.reduce((sum, r) => sum + r.totalScore, 0);
        const relativeToPar = rounds.reduce((sum, r) => sum + r.relativeToPar, 0);

        return {
            golferId: golfer.id,
            golferName: golfer.name,
            nationality: golfer.nationality,
            handicapIndex: golfer.handicapIndex,
            totalStrokes,
            relativeToPar,
            rounds: rounds.map(r => r.totalScore),
            roundsPlayed: rounds.length,
            thru: 18,  // Full rounds only for now
            currentRound: rounds.length > 0 ? rounds[rounds.length - 1] : null
        };
    });

    // Sort by total strokes (lower is better)
    aggregated.sort((a, b) => a.totalStrokes - b.totalStrokes);

    // Assign positions (handle ties)
    let currentPosition = 1;
    let currentScore = null;
    let playersAtScore = 0;

    aggregated.forEach((player, index) => {
        if (player.totalStrokes !== currentScore) {
            currentPosition = index + 1;
            currentScore = player.totalStrokes;
            playersAtScore = 1;
        } else {
            playersAtScore++;
        }
        player.position = currentPosition;
        player.tied = playersAtScore > 1;
    });

    // Calculate strokes behind leader
    const leaderScore = aggregated[0]?.totalStrokes || 0;
    aggregated.forEach(player => {
        player.strokesBehind = player.totalStrokes - leaderScore;
    });

    return aggregated;
}

/**
 * Apply cut to field
 */
function applyCut(standings, cutRule, cutPosition) {
    if (standings.length === 0) return { madeCut: [], missedCut: [], cutLine: null };

    // Find cut line score
    let cutLineScore;
    let cutLinePosition;

    switch (cutRule) {
        case "bottomPercentage": {
            // cutPosition holds the percentage to cut (e.g. 40 means cut bottom 40%)
            const keepCount = Math.ceil(standings.length * (1 - cutPosition / 100));
            const targetPos = Math.max(1, keepCount);
            const atOrAbove = standings.filter(p => p.position <= targetPos);
            if (atOrAbove.length > 0) {
                cutLineScore = Math.max(...atOrAbove.map(p => p.totalStrokes));
                cutLinePosition = targetPos;
            } else {
                cutLineScore = standings[standings.length - 1].totalStrokes;
                cutLinePosition = standings.length;
            }
            break;
        }
        case "top70AndTies":
        case "top50AndTies":
        case "topN": {
            const targetPosition = cutRule === "top50AndTies" ? 50 : cutPosition;
            // Find the score at the target position
            const playersAtOrAbove = standings.filter(p => p.position <= targetPosition);
            if (playersAtOrAbove.length > 0) {
                cutLineScore = Math.max(...playersAtOrAbove.map(p => p.totalStrokes));
                cutLinePosition = targetPosition;
            } else {
                cutLineScore = standings[standings.length - 1].totalStrokes;
                cutLinePosition = standings.length;
            }
            break;
        }
        default:
            cutLineScore = standings[Math.min(cutPosition - 1, standings.length - 1)].totalStrokes;
            cutLinePosition = cutPosition;
    }

    const madeCut = standings.filter(p => p.totalStrokes <= cutLineScore);
    const missedCut = standings.filter(p => p.totalStrokes > cutLineScore);

    return {
        madeCut,
        missedCut,
        cutLine: {
            position: cutLinePosition,
            score: cutLineScore - (standings[0]?.rounds?.length || 2) * 72,  // Relative to par
            playersMadeCut: madeCut.length
        }
    };
}

/**
 * Simulate a complete tournament
 */
export function simulateTournament(golfers, course, config) {
    const tournament = createTournamentConfig(config);
    const roundResults = [];
    let activeGolfers = [...golfers];
    let standings = [];
    let cutInfo = null;

    // Generate weather conditions if not provided
    const conditions = tournament.conditions ||
        Array.from({ length: tournament.rounds }, (_, i) => ({
            round: i + 1,
            wind: 5 + Math.random() * 15,
            rain: Math.random() < (course.weatherExposure * 0.3),
            temperature: 60 + Math.random() * 25
        }));

    // Simulate each round
    for (let roundNum = 1; roundNum <= tournament.rounds; roundNum++) {
        const roundConditions = conditions[roundNum - 1];

        // Calculate current standings for pressure calculation
        standings = calculateStandings(activeGolfers, roundResults, course);

        // Simulate round for each active golfer
        activeGolfers.forEach(golfer => {
            const currentPosition = standings.find(s => s.golferId === golfer.id)?.position || activeGolfers.length;

            const roundContext = {
                roundNumber: roundNum,
                position: currentPosition,
                totalRounds: tournament.rounds,
                prestigeLevel: tournament.prestigeLevel
            };

            const roundResult = simulateTournamentRound(golfer, course, roundConditions, roundContext);
            roundResults.push(roundResult);
        });

        // Update standings after round
        standings = calculateStandings(activeGolfers, roundResults, course);

        // Apply cut after designated round
        if (roundNum === tournament.cutAfterRound && tournament.rounds > 2) {
            cutInfo = applyCut(standings, tournament.cutRule, tournament.cutPosition);
            activeGolfers = golfers.filter(g =>
                cutInfo.madeCut.some(m => m.golferId === g.id)
            );

            // Mark cut status in standings
            standings.forEach(s => {
                s.cutStatus = cutInfo.madeCut.some(m => m.golferId === s.golferId) ? "made" : "missed";
            });
        }
    }

    // Final standings — rank made-cut players by total strokes,
    // then append missed-cut players ranked by their score at the cut
    if (cutInfo) {
        const madeCutIds = new Set(cutInfo.madeCut.map(m => m.golferId));
        const madeCutGolfers = golfers.filter(g => madeCutIds.has(g.id));
        const missedCutGolfers = golfers.filter(g => !madeCutIds.has(g.id));

        const madeCutStandings = calculateStandings(madeCutGolfers, roundResults, course);
        madeCutStandings.forEach(s => { s.cutStatus = "made"; });

        const missedCutStandings = calculateStandings(missedCutGolfers, roundResults, course);
        const nextPosition = madeCutStandings.length + 1;
        missedCutStandings.forEach((s, i) => {
            s.cutStatus = "missed";
            s.position = nextPosition + i;
        });

        standings = [...madeCutStandings, ...missedCutStandings];
    } else {
        standings = calculateStandings(golfers, roundResults, course);
        standings.forEach(s => { s.cutStatus = "made"; });
    }

    return {
        tournament: {
            ...tournament,
            conditions
        },
        course: {
            id: course.id,
            name: course.name,
            par: course.par
        },
        standings,
        roundResults,
        cutInfo,
        winner: standings[0] || null
    };
}

/**
 * Simulate multiple tournaments for statistical analysis
 */
export function simulateMultipleTournaments(golfers, course, config, count = 100) {
    const results = [];
    const winCounts = {};
    const top6Counts = {};
    const top10Counts = {};
    const madeCutCounts = {};
    const totalScores = {};  // Track cumulative scores
    const roundCounts = {};  // Track rounds played (for average)

    // Track per-round scores for trend analysis
    const roundScores = {};  // { golferId: { 1: [scores], 2: [scores], ... } }
    const roundScoresMadeCut = {};  // Same but only for golfers who made the cut
    const roundWeather = { 1: [], 2: [], 3: [], 4: [] };  // Track weather per round

    golfers.forEach(g => {
        winCounts[g.id] = 0;
        top6Counts[g.id] = 0;
        top10Counts[g.id] = 0;
        madeCutCounts[g.id] = 0;
        totalScores[g.id] = 0;
        roundCounts[g.id] = 0;
        roundScores[g.id] = { 1: [], 2: [], 3: [], 4: [] };
        roundScoresMadeCut[g.id] = { 1: [], 2: [], 3: [], 4: [] };
    });

    const tournamentRounds = config.rounds || 4;

    for (let i = 0; i < count; i++) {
        const result = simulateTournament(golfers, course, config);
        results.push(result);

        // Track weather conditions per round
        if (result.tournament?.conditions) {
            result.tournament.conditions.forEach(c => {
                if (c.round <= 4) {
                    roundWeather[c.round].push(c.wind);
                }
            });
        }

        // Track statistics
        if (result.winner) {
            winCounts[result.winner.golferId] = (winCounts[result.winner.golferId] || 0) + 1;
        }

        // Build set of golfers who made the cut in this simulation
        const madeCutIds = new Set(
            result.standings.filter(s => s.cutStatus === "made").map(s => s.golferId)
        );

        result.standings.forEach(s => {
            if (s.position <= 6) {
                top6Counts[s.golferId] = (top6Counts[s.golferId] || 0) + 1;
            }
            if (s.position <= 10) {
                top10Counts[s.golferId] = (top10Counts[s.golferId] || 0) + 1;
            }
            if (s.cutStatus === "made") {
                madeCutCounts[s.golferId] = (madeCutCounts[s.golferId] || 0) + 1;
            }

            // Track scores (use per-round scores from rounds array)
            if (s.rounds && s.rounds.length > 0) {
                s.rounds.forEach((roundScore, idx) => {
                    totalScores[s.golferId] += roundScore;
                    roundCounts[s.golferId] += 1;
                    // Track by round number (1-indexed)
                    const roundNum = idx + 1;
                    if (roundNum <= 4) {
                        roundScores[s.golferId][roundNum].push(roundScore);
                        // Also track for made-cut cohort
                        if (madeCutIds.has(s.golferId)) {
                            roundScoresMadeCut[s.golferId][roundNum].push(roundScore);
                        }
                    }
                });
            }
        });
    }

    // Calculate expected score per round for each golfer
    const getExpectedScore = (golfer) => {
        const courseHandicap = calculateCourseHandicap(
            golfer.handicapIndex,
            course.slopeRating,
            course.courseRating,
            course.par
        );
        return course.par + courseHandicap;
    };

    // Helper to calculate average of array
    const avg = (arr) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : null;

    // Calculate percentages and averages
    const golferStats = golfers.map(g => {
        const avgScore = roundCounts[g.id] > 0
            ? totalScores[g.id] / roundCounts[g.id]
            : null;
        const expectedScore = getExpectedScore(g);

        // Per-round averages
        const perRound = {};
        for (let r = 1; r <= tournamentRounds; r++) {
            const scores = roundScores[g.id][r];
            perRound[r] = scores.length > 0 ? avg(scores) : null;
        }

        return {
            golfer: g,
            wins: winCounts[g.id] || 0,
            winPercentage: ((winCounts[g.id] || 0) / count * 100).toFixed(1),
            top6s: top6Counts[g.id] || 0,
            top6Percentage: ((top6Counts[g.id] || 0) / count * 100).toFixed(1),
            top10s: top10Counts[g.id] || 0,
            top10Percentage: ((top10Counts[g.id] || 0) / count * 100).toFixed(1),
            madeCuts: madeCutCounts[g.id] || 0,
            madeCutPercentage: ((madeCutCounts[g.id] || 0) / count * 100).toFixed(1),
            avgScore: avgScore !== null ? avgScore.toFixed(1) : '-',
            expectedScore: expectedScore,
            scoreDiff: avgScore !== null ? (avgScore - expectedScore).toFixed(1) : '-',
            perRoundAvg: perRound
        };
    });

    // Calculate overall round averages (all golfers combined) and weather
    const roundSummary = {};
    const madeCutRoundSummary = {};
    for (let r = 1; r <= tournamentRounds; r++) {
        const allScoresForRound = golfers.flatMap(g => roundScores[g.id][r]);
        const madeCutScoresForRound = golfers.flatMap(g => roundScoresMadeCut[g.id][r]);
        const avgWind = roundWeather[r].length > 0 ? avg(roundWeather[r]) : null;
        roundSummary[r] = {
            avgScore: allScoresForRound.length > 0 ? avg(allScoresForRound).toFixed(1) : '-',
            avgWind: avgWind !== null ? avgWind.toFixed(1) : '-',
            sampleSize: allScoresForRound.length
        };
        madeCutRoundSummary[r] = {
            avgScore: madeCutScoresForRound.length > 0 ? avg(madeCutScoresForRound).toFixed(1) : '-',
            avgWind: avgWind !== null ? avgWind.toFixed(1) : '-',
            sampleSize: madeCutScoresForRound.length
        };
    }

    // Sort by wins desc, then T6 desc, then made cut desc
    golferStats.sort((a, b) =>
        b.wins - a.wins
        || b.top6s - a.top6s
        || b.madeCuts - a.madeCuts
    );

    return {
        simulations: count,
        tournamentRounds,
        course: {
            name: course.name,
            par: course.par,
            courseRating: course.courseRating,
            slopeRating: course.slopeRating
        },
        roundSummary,
        madeCutRoundSummary,
        golferStats,
        results
    };
}

/**
 * Aggregate an array of raw tournament results into stats.
 * Accepts the output of multiple simulateTournament() calls.
 */
export function aggregateSimulationResults(results, golfers, course, config) {
    const count = results.length;
    const tournamentRounds = config.rounds || 4;

    const winCounts = {};
    const top6Counts = {};
    const top10Counts = {};
    const madeCutCounts = {};
    const totalScores = {};
    const roundCounts = {};
    const roundScores = {};
    const roundScoresMadeCut = {};
    const roundWeather = { 1: [], 2: [], 3: [], 4: [] };

    golfers.forEach(g => {
        winCounts[g.id] = 0;
        top6Counts[g.id] = 0;
        top10Counts[g.id] = 0;
        madeCutCounts[g.id] = 0;
        totalScores[g.id] = 0;
        roundCounts[g.id] = 0;
        roundScores[g.id] = { 1: [], 2: [], 3: [], 4: [] };
        roundScoresMadeCut[g.id] = { 1: [], 2: [], 3: [], 4: [] };
    });

    results.forEach(result => {
        if (result.tournament?.conditions) {
            result.tournament.conditions.forEach(c => {
                if (c.round <= 4) roundWeather[c.round].push(c.wind);
            });
        }

        if (result.winner) {
            winCounts[result.winner.golferId] = (winCounts[result.winner.golferId] || 0) + 1;
        }

        const madeCutIds = new Set(
            result.standings.filter(s => s.cutStatus === "made").map(s => s.golferId)
        );

        result.standings.forEach(s => {
            if (s.position <= 6) top6Counts[s.golferId] = (top6Counts[s.golferId] || 0) + 1;
            if (s.position <= 10) top10Counts[s.golferId] = (top10Counts[s.golferId] || 0) + 1;
            if (s.cutStatus === "made") madeCutCounts[s.golferId] = (madeCutCounts[s.golferId] || 0) + 1;

            if (s.rounds && s.rounds.length > 0) {
                s.rounds.forEach((roundScore, idx) => {
                    totalScores[s.golferId] += roundScore;
                    roundCounts[s.golferId] += 1;
                    const roundNum = idx + 1;
                    if (roundNum <= 4) {
                        roundScores[s.golferId][roundNum].push(roundScore);
                        if (madeCutIds.has(s.golferId)) {
                            roundScoresMadeCut[s.golferId][roundNum].push(roundScore);
                        }
                    }
                });
            }
        });
    });

    const getExpectedScore = (golfer) => {
        const courseHandicap = calculateCourseHandicap(
            golfer.handicapIndex, course.slopeRating, course.courseRating, course.par
        );
        return course.par + courseHandicap;
    };

    const avg = (arr) => arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : null;

    const golferStats = golfers.map(g => {
        const avgScore = roundCounts[g.id] > 0 ? totalScores[g.id] / roundCounts[g.id] : null;
        const expectedScore = getExpectedScore(g);
        const perRound = {};
        for (let r = 1; r <= tournamentRounds; r++) {
            const scores = roundScores[g.id][r];
            perRound[r] = scores.length > 0 ? avg(scores) : null;
        }

        return {
            golfer: g,
            wins: winCounts[g.id] || 0,
            winPercentage: ((winCounts[g.id] || 0) / count * 100).toFixed(1),
            top6s: top6Counts[g.id] || 0,
            top6Percentage: ((top6Counts[g.id] || 0) / count * 100).toFixed(1),
            top10s: top10Counts[g.id] || 0,
            top10Percentage: ((top10Counts[g.id] || 0) / count * 100).toFixed(1),
            madeCuts: madeCutCounts[g.id] || 0,
            madeCutPercentage: ((madeCutCounts[g.id] || 0) / count * 100).toFixed(1),
            avgScore: avgScore !== null ? avgScore.toFixed(1) : '-',
            expectedScore: expectedScore,
            scoreDiff: avgScore !== null ? (avgScore - expectedScore).toFixed(1) : '-',
            perRoundAvg: perRound
        };
    });

    const roundSummary = {};
    const madeCutRoundSummary = {};
    for (let r = 1; r <= tournamentRounds; r++) {
        const allScoresForRound = golfers.flatMap(g => roundScores[g.id][r]);
        const madeCutScoresForRound = golfers.flatMap(g => roundScoresMadeCut[g.id][r]);
        const avgWind = roundWeather[r].length > 0 ? avg(roundWeather[r]) : null;
        roundSummary[r] = {
            avgScore: allScoresForRound.length > 0 ? avg(allScoresForRound).toFixed(1) : '-',
            avgWind: avgWind !== null ? avgWind.toFixed(1) : '-',
            sampleSize: allScoresForRound.length
        };
        madeCutRoundSummary[r] = {
            avgScore: madeCutScoresForRound.length > 0 ? avg(madeCutScoresForRound).toFixed(1) : '-',
            avgWind: avgWind !== null ? avgWind.toFixed(1) : '-',
            sampleSize: madeCutScoresForRound.length
        };
    }

    golferStats.sort((a, b) =>
        b.wins - a.wins || b.top6s - a.top6s || b.madeCuts - a.madeCuts
    );

    return {
        simulations: count,
        tournamentRounds,
        course: { name: course.name, par: course.par, courseRating: course.courseRating, slopeRating: course.slopeRating },
        roundSummary,
        madeCutRoundSummary,
        golferStats,
        results
    };
}

/**
 * Format score for display (e.g., -5, E, +3)
 */
export function formatScore(relativeToPar) {
    if (relativeToPar === 0) return "E";
    if (relativeToPar > 0) return `+${relativeToPar}`;
    return String(relativeToPar);
}

/**
 * Format position for display (handles ties)
 */
export function formatPosition(position, tied) {
    return tied ? `T${position}` : String(position);
}
